import { useRef, type ReactNode } from "react";
import { Text } from "@softmax/design-system";
import * as stylex from "@stylexjs/stylex";
import { EmbedFullscreenButton } from "./embed-fullscreen-button";
import { replayStyles } from "./replay.styles";
import type { ReplayViewerKind } from "./replay-viewer-links";

export function EmbeddedReplayViewer({
  viewer,
  viewerUrl,
  frameHeight,
  hasCoworld,
  label,
  renderCoworldFrame,
}: Readonly<{
  hasCoworld: boolean;
  label: string | undefined;
  viewer: ReplayViewerKind;
  viewerUrl: string | null;
  frameHeight: string;
  renderCoworldFrame: (height: string) => ReactNode;
}>) {
  const frameRef = useRef<HTMLDivElement>(null);

  if (viewer === "coworld") {
    if (viewerUrl && hasCoworld) return renderCoworldFrame(frameHeight);

    return (
      <Text size="small" tone="muted">
        Replay job unavailable.
      </Text>
    );
  }

  if (!viewerUrl) return null;

  return (
    <div
      ref={frameRef}
      {...stylex.props(
        replayStyles.frame,
        replayStyles.bordered,
        replayStyles.viewer,
      )}
      style={{ height: frameHeight, minHeight: "240px" }}
    >
      <iframe
        src={viewerUrl}
        title={label ?? "Episode replay"}
        {...stylex.props(replayStyles.iframeElement, replayStyles.iframe)}
        // Both viewer URLs use fixed origins separate from the app and need their asset origins.
        // oxlint-disable-next-line react/iframe-missing-sandbox -- The fixed content origins need scripts and same-origin assets.
        sandbox="allow-scripts allow-same-origin allow-downloads"
        allowFullScreen
      />
      <EmbedFullscreenButton targetRef={frameRef} />
    </div>
  );
}
